import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { WpsData } from '../../types'
import { resolvePassSectionSummary } from '../../utils/passSequenceExpanded'
import { isUserAccompanyingReport } from '../../utils/wpsSectionPersistence'
import { buildDefaultWpsNumber } from '../../utils/wpsNumberDefaults'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'

export default function WpsForm() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getProject } = useProjects()
  const { loading: sectionsLoading, getSection, updateSection } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const returnToParam = searchParams.get('returnTo')
  const returnTo = returnToParam && returnToParam.trim() !== '' ? returnToParam : null

  // Slice 06 of `wps-preview-pixel-parity`. When the user clicks the
  // Notes paragraph on the WPS Preview canvas they're routed here
  // with `?focusField=notesTextarea&returnTo=…`. We scroll the
  // shared comments/notes textarea into view, flash it amber, and
  // call `.focus()` so the cursor is ready for the user to type.
  // The ref is attached to the existing textarea below.
  // PQR Preview Comments uses the same marker via comments-wpsform.
  const notesTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  useFieldFocusHandler({
    ready: !sectionsLoading,
    resolveTarget: (params) =>
      params.focusField === 'notesTextarea' ? notesTextareaRef.current : null
  })

  const [wpsData, setWpsData] = useState<WpsData>({
    id: `wps-${pipeId}`,
    wpsNumber: buildDefaultWpsNumber(project?.name, pipeId),
    revision: 'A',
    date: new Date().toISOString().split('T')[0],
    status: 'Draft',
    projectId: id || '',
    pipeSetId: pipeId || '',
    applicableCode: 'ASME B31.3 / API 1104',
    baseMaterial: '',
    thicknessRange: pipeSet
      ? `${pipeSet.wallThickness} ${pipeSet.wallThicknessUnit}`
      : '',
    jointDesign: {
      jointType: '',
      backingMaterial: '',
      backingType: ''
    },
    position: '',
    weldingProcess: '',
    weldingParameters: [
      {
        pass: 'Root',
        process: 'GMAW',
        currentMin: '',
        currentMax: '',
        voltageMin: '',
        voltageMax: '',
        travelSpeedMin: '',
        travelSpeedMax: ''
      }
    ],
    thermalRequirements: {
      pwhtRequired: false
    },
    electricalParameters: {
      polarity: 'DCEP',
      powerSource: 'CV'
    },
    consumables: []
  })

  const [welderDetails, setWelderDetails] = useState({
    welderName: '',
    welderId: '',
    operator: ''
  })
  const [weldTimes, setWeldTimes] = useState({
    root: '',
    fill: '',
    cap: '',
    total: ''
  })
  const [comments, setComments] = useState('')
  const [passSummary, setPassSummary] = useState({
    sequenceLabel: '',
    passCount: ''
  })
  const [preheatSummary, setPreheatSummary] = useState({
    preheatUnit: '°F',
    interpassUnit: '°F'
  })
  const [clampSummary, setClampSummary] = useState({
    clampType: '',
    clampCount: '',
    removalNote: '',
    rootPowerSupplies: '',
    otherPowerSupplies: ''
  })
  const [bevelSummary, setBevelSummary] = useState({
    bevelCount: 0
  })
  const [sectionCompletion, setSectionCompletion] = useState({
    base: false,
    bevel: false,
    pass: false,
    preheat: false,
    clamp: false,
    weldTimes: false,
    postWeld: false
  })
  const [weldTimesSummary, setWeldTimesSummary] = useState({
    maxRootToHot: '',
    maxHotToFill: '',
    maxRootToHotUnit: 'Hours',
    maxHotToFillUnit: 'Hours'
  })
  const [postWeldSummary, setPostWeldSummary] = useState({
    postHeating: '',
    pwht: '',
    coolingMethod: '',
    cleaningNotes: ''
  })
  const [reports, setReports] = useState<Array<{ name: string; tag: string }>>([])
  const [savedMetaCommentsJson, setSavedMetaCommentsJson] = useState('')
  const [saving, setSaving] = useState(false)

  const metaCommentsPayload = useMemo(
    () => JSON.stringify({ wpsNumber: wpsData.wpsNumber, comments }),
    [wpsData.wpsNumber, comments]
  )

  const isDirty = useMemo(
    () => savedMetaCommentsJson !== '' && metaCommentsPayload !== savedMetaCommentsJson,
    [metaCommentsPayload, savedMetaCommentsJson]
  )

  const completionTotal = Object.values(sectionCompletion).filter(Boolean).length
  const completionTarget = Object.keys(sectionCompletion).length
  const clientLocationLine = project ? formatClientLocationSubtitle(project) : null

  // Clear before hydrate. PipeSetSectionsOutlet only mounts this page after
  // sections finish loading, so both effects run on first paint — if clear
  // runs after hydrate, savedMetaCommentsJson stays '' and Save never enables.
  useEffect(() => {
    setSavedMetaCommentsJson('')
  }, [pipeId])

  useEffect(() => {
    if (!pipeId || sectionsLoading) return

    const meta = getSection('meta')
    const base = getSection('base')
    const bevel = getSection('bevel')
    const pass = getSection('pass')
    const preheat = getSection('preheat')
    const clamp = getSection('clamp')
    const weldTimes = getSection('weldTimes')
    const postWeld = getSection('postWeld')
    const cleaning = getSection('cleaningGrinding')
    const reportData = getSection('reports')
    const welder = getSection('welder')
    const times = getSection('weldTimes')
    const notes = getSection('comments')

    const bevelCount = Array.isArray(bevel?.bevels) ? bevel.bevels.length : 0
    setBevelSummary({ bevelCount })

    const primaryLot = base?.manufacturerLots?.[0]
    const derivedBaseMaterial =
      primaryLot?.materialSpec?.length || primaryLot?.typeGrade?.length
        ? `${primaryLot?.materialSpec?.[0] ?? ''}${primaryLot?.typeGrade?.[0] ? ` / ${primaryLot?.typeGrade?.[0]}` : ''}`.trim()
        : ''
    const pipeSetForRange = project?.pipeSets?.find((p) => p.id === pipeId)
    const derivedThicknessRange = pipeSetForRange
      ? `${pipeSetForRange.wallThickness} ${pipeSetForRange.wallThicknessUnit}`
      : ''

    setWpsData((prev) => ({
      ...prev,
      wpsNumber: meta?.wpsNumber ?? prev.wpsNumber,
      baseMaterial: derivedBaseMaterial || base?.baseMaterial || prev.baseMaterial,
      thicknessRange: derivedThicknessRange || base?.thicknessRange || prev.thicknessRange,
      jointDesign: {
        ...prev.jointDesign,
        jointType:
          bevel?.bevels?.[0]?.jointType?.[0] ??
          (Array.isArray(bevel?.jointType) ? bevel.jointType[0] : bevel?.jointType) ??
          prev.jointDesign.jointType,
        backingType:
          bevel?.bevels?.[0]?.backingType?.[0] ??
          (Array.isArray(bevel?.backingType) ? bevel.backingType[0] : bevel?.backingType) ??
          prev.jointDesign.backingType,
        backingMaterial:
          bevel?.bevels?.[0]?.backingMaterial?.[0] ??
          (Array.isArray(bevel?.backingMaterial) ? bevel.backingMaterial[0] : bevel?.backingMaterial) ??
          prev.jointDesign.backingMaterial
      },
      thermalRequirements: {
        ...prev.thermalRequirements,
        preheatMin: preheat?.preheatMin ?? prev.thermalRequirements.preheatMin,
        preheatMax: preheat?.preheatMax ?? prev.thermalRequirements.preheatMax,
        interpassMin: preheat?.interpassMin ?? prev.thermalRequirements.interpassMin,
        interpassMax: preheat?.interpassMax ?? prev.thermalRequirements.interpassMax
      }
    }))
    const passSummaryResolved = resolvePassSectionSummary(pass)
    setPassSummary({
      sequenceLabel: passSummaryResolved.sequenceLabel,
      passCount: passSummaryResolved.passCount
    })
    if (preheat) {
      const normalizeUnit = (value: string | undefined) =>
        value === '°C' || value === '°F' ? value : '°F'
      setPreheatSummary({
        preheatUnit: normalizeUnit(preheat.preheatUnit),
        interpassUnit: normalizeUnit(preheat.interpassUnit)
      })
    }
    if (clamp) {
      setClampSummary({
        clampType: (clamp.clampType || [])[0] || '',
        clampCount: clamp.clampCount || '',
        removalNote: clamp.removalNote || '',
        rootPowerSupplies: (clamp.rootPowerSupplies || []).join(', '),
        otherPowerSupplies: (clamp.otherPowerSupplies || []).join(', ')
      })
    }
    if (weldTimes) {
      setWeldTimesSummary({
        maxRootToHot: weldTimes.maxRootToHot || '',
        maxHotToFill: weldTimes.maxHotToFill || '',
        maxRootToHotUnit: weldTimes.maxRootToHotUnit || 'Hours',
        maxHotToFillUnit: weldTimes.maxHotToFillUnit || 'Hours'
      })
    }
    if (postWeld || cleaning) {
      setPostWeldSummary({
        postHeating: (postWeld?.postHeating || [])[0] || '',
        pwht: (postWeld?.pwht || [])[0] || '',
        coolingMethod: (postWeld?.coolingMethod || [])[0] || '',
        cleaningNotes: postWeld?.cleaningNotes || cleaning?.cleaningNotes || ''
      })
    }
    if (welder) {
      setWelderDetails({
        welderName: welder.welderName ?? '',
        welderId: welder.welderId ?? '',
        operator: welder.operator ?? ''
      })
    }
    if (times) {
      setWeldTimes({
        root: times.root ?? '',
        fill: times.fill ?? '',
        cap: times.cap ?? '',
        total: times.total ?? ''
      })
    }
    if (notes?.comments) {
      setComments(notes.comments)
    }
    if (reportData) {
      // Mirror the Accompanying Reports list filter (see
      // `isUserAccompanyingReport` in utils/wpsSectionPersistence.ts):
      // user-uploaded supporting documents only — hide soft-deleted
      // entries and auto-generated WPS/PQR PDFs (dedicated history views).
      const rawItems = Array.isArray(reportData.items) ? reportData.items : []
      setReports(rawItems.filter(isUserAccompanyingReport))
    }

    setSavedMetaCommentsJson(
      JSON.stringify({
        wpsNumber: meta?.wpsNumber != null ? String(meta.wpsNumber) : wpsData.wpsNumber,
        comments: typeof notes?.comments === 'string' ? notes.comments : ''
      })
    )

    const hasValue = (value: any) =>
      value !== undefined && value !== null && String(value).trim() !== ''
    const hasList = (value: any) => Array.isArray(value) && value.length > 0
    const hasClampType = (value: unknown) =>
      hasList(value) || (typeof value === 'string' && value.trim() !== '')
    setSectionCompletion({
      base: hasList(primaryLot?.materialSpec) && hasList(primaryLot?.typeGrade),
      bevel:
        (Array.isArray(bevel?.bevels) &&
          bevel.bevels.length > 0 &&
          hasList(bevel.bevels[0]?.jointType) &&
          hasList(bevel.bevels[0]?.backingType)) ||
        (hasList(bevel?.jointType) && hasList(bevel?.backingType)),
      pass: passSummaryResolved.isComplete,
      preheat: hasValue(preheat?.preheatMin) && hasValue(preheat?.interpassMin),
      clamp: hasClampType(clamp?.clampType) && hasValue(clamp?.clampCount),
      weldTimes:
        (hasValue(weldTimes?.maxRootToHot) || String(weldTimes?.maxRootToHot ?? '').trim() === '-') &&
        (hasValue(weldTimes?.maxHotToFill) || String(weldTimes?.maxHotToFill ?? '').trim() === '-'),
      postWeld:
        hasList(postWeld?.postHeating) ||
        hasList(postWeld?.pwht) ||
        hasList(postWeld?.coolingMethod) ||
        hasValue(postWeld?.cleaningNotes) ||
        hasValue(cleaning?.cleaningNotes)
      })
  }, [pipeId, sectionsLoading, getSection])

  const handleWpsNumberChange = (value: string) => {
    setWpsData((prev) => ({ ...prev, wpsNumber: value }))
  }

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    setSaving(true)
    try {
      await updateSection('meta', { wpsNumber: wpsData.wpsNumber })
      await updateSection('comments', { comments })
      setSavedMetaCommentsJson(JSON.stringify({ wpsNumber: wpsData.wpsNumber, comments }))
      navigate(-1)
    } catch {
      // surfaced by context
    } finally {
      setSaving(false)
    }
  }

  if (!project || !pipeSet) {
    return (
      <Card title="WPS not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={returnTo} contextLabel="Comments" />
      <PageHeader
        backTo={{
          to: returnTo ?? `/projects/${project.id}/next`,
          label: returnTo ? backToLabelFor(returnTo) : 'Back to Pipe Sets'
        }}
        title={<ProjectPageTitle label="WPS Details" projectName={project.name} />}
        subtitle={
          <>
            {clientLocationLine ? <span>{clientLocationLine}</span> : null}
            <span>
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </span>
            <Badge
              tone={
                wpsData.status === 'Draft'
                  ? 'warning'
                  : wpsData.status === 'Approved'
                    ? 'success'
                    : 'gray'
              }
            >
              {wpsData.status}
            </Badge>
            <Badge tone={completionTotal === completionTarget ? 'success' : 'warning'}>
              {completionTotal}/{completionTarget} complete
            </Badge>
          </>
        }
        actions={
          <>
            <div className="flex h-9 items-center gap-2 rounded-md border border-[#E2E5EA] bg-white px-3 shadow-sm">
              <span className="text-[11px] font-medium text-heritage-grey">WPS ID</span>
              <input
                className="w-28 border-0 bg-transparent text-xs font-semibold text-text-grey focus:outline-none"
                value={wpsData.wpsNumber}
                onChange={(event) => handleWpsNumberChange(event.target.value)}
                placeholder="Enter WPS ID"
              />
            </div>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save WPS
            </Button>
          </>
        }
      />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Base Material Details</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {wpsData.baseMaterial || 'Not set'}
              </p>
            </div>
            <Badge tone={sectionCompletion.base ? 'success' : 'gray'}>
              {sectionCompletion.base ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            OD: {pipeSet.nominalOd} {pipeSet.nominalOdUnit} • WT: {wpsData.thicknessRange || 'Not set'}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/base`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Bevel Details</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {bevelSummary.bevelCount === 0
                  ? 'Not set'
                  : bevelSummary.bevelCount === 1
                    ? '1 bevel planned'
                    : `${bevelSummary.bevelCount} bevels planned`}
              </p>
            </div>
            <Badge tone={sectionCompletion.bevel ? 'success' : 'gray'}>
              {sectionCompletion.bevel ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/bevel`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Pass Details</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {passSummary.sequenceLabel || 'Not set'}
              </p>
            </div>
            <Badge tone={sectionCompletion.pass ? 'success' : 'gray'}>
              {sectionCompletion.pass ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            Passes: {passSummary.passCount || '0'}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/pass`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Preheat / Interpass</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {wpsData.thermalRequirements.preheatMin || '--'} – {wpsData.thermalRequirements.preheatMax || '--'} {preheatSummary.preheatUnit}
              </p>
            </div>
            <Badge tone={sectionCompletion.preheat ? 'success' : 'gray'}>
              {sectionCompletion.preheat ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            Interpass: {wpsData.thermalRequirements.interpassMin || '--'} – {wpsData.thermalRequirements.interpassMax || '--'} {preheatSummary.interpassUnit}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/preheat`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Equipment Details</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {clampSummary.clampType || 'Not set'}
              </p>
            </div>
            <Badge tone={sectionCompletion.clamp ? 'success' : 'gray'}>
              {sectionCompletion.clamp ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            Count: {clampSummary.clampCount || '--'}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/clamp`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Weld Times</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {weldTimesSummary.maxRootToHot && weldTimesSummary.maxRootToHot !== '-'
                  ? `${weldTimesSummary.maxRootToHot} ${weldTimesSummary.maxRootToHotUnit}`
                  : '--'}
              </p>
            </div>
            <Badge tone={sectionCompletion.weldTimes ? 'success' : 'gray'}>
              {sectionCompletion.weldTimes ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            Hot → Fill 1:{' '}
            {weldTimesSummary.maxHotToFill && weldTimesSummary.maxHotToFill !== '-'
              ? `${weldTimesSummary.maxHotToFill} ${weldTimesSummary.maxHotToFillUnit}`
              : '--'}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/weld-times`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] text-gray-500">Post Weld Activities</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {postWeldSummary.postHeating || 'Not set'}
              </p>
            </div>
            <Badge tone={sectionCompletion.postWeld ? 'success' : 'gray'}>
              {sectionCompletion.postWeld ? 'Complete' : 'Needs info'}
            </Badge>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            PWHT: {postWeldSummary.pwht || '--'} • Cooling: {postWeldSummary.coolingMethod || '--'}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            Cleaning: {postWeldSummary.cleaningNotes ? 'Captured' : '--'}
          </div>
          <div className="mt-auto flex items-center justify-end pt-3">
            <Link to={`/projects/${project.id}/next/${pipeId}/post-weld`}>
              <Button className="h-8 px-2.5 text-xs">View details</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold text-gray-700">Process Map</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="rounded-full bg-gray-100 px-3 py-1">Floor Sheet</span>
          <span>→</span>
          <span className="rounded-full bg-gray-100 px-3 py-1">PQR</span>
          <span>→</span>
          <span className="rounded-full bg-primary-50 px-3 py-1 text-primary-700">WPS</span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-gray-500">Floor Sheet</p>
          <p className="mt-1 text-base font-semibold text-gray-900">Draft floor sheet</p>
          <p className="mt-2 text-[11px] text-gray-500">Weld map and joint design.</p>
          <div className="mt-3 flex items-center justify-end">
            <Link to={`/projects/${project.id}/next/${pipeId}/floorsheet`}>
              <Button className="h-8 px-2.5 text-xs" variant="secondary">Open Floor Sheet</Button>
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-gray-500">PQR</p>
          <p className="mt-1 text-base font-semibold text-gray-900">Procedure Qualification Record</p>
          <p className="mt-2 text-[11px] text-gray-500">Generate PQR PDF for this pipe set.</p>
          <div className="mt-3 flex items-center justify-end">
            <Link to={`/projects/${project.id}/next/${pipeId}/pqr`}>
              <Button className="h-8 px-2.5 text-xs" variant="secondary">Open PQR</Button>
            </Link>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-gray-500">WPS</p>
          <p className="mt-1 text-base font-semibold text-gray-900">Current WPS</p>
          <p className="mt-2 text-[11px] text-gray-500">Review or update details.</p>
          <div className="mt-3 flex items-center justify-end gap-2">
            <Link
              to={`/projects/${project.id}/next/${pipeId}/wps/reports`}
              data-testid="current-wps-open-history"
            >
              <Button className="h-8 px-2.5 text-xs" variant="secondary">Open WPS</Button>
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-gray-500">Mechanical tests</p>
          <p className="mt-1 text-base font-semibold text-gray-900">Test reports</p>
          <p className="mt-2 text-[11px] text-gray-500">Mechanical test results for this project.</p>
          <div className="mt-3 flex items-center justify-end">
            <Link to={`/projects/${project.id}/mechanical-tests`}>
              <Button className="h-8 px-2.5 text-xs" variant="secondary">Open reports</Button>
            </Link>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">Accompanying Reports</p>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {reports.length ? `${reports.length} document${reports.length > 1 ? 's' : ''}` : 'No documents'}
              </p>
              <p className="mt-2 text-[11px] text-gray-500">Upload and tag supporting documents.</p>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end">
            <Link to={`/projects/${project.id}/next/${pipeId}/reports`}>
              <Button className="h-8 px-2.5 text-xs" variant="secondary">Open Reports</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold text-gray-700">Comments</p>
        <textarea
          ref={notesTextareaRef}
          data-testid="wps-form-notes-textarea"
          className="input-primary mt-3 h-28"
          placeholder="Add notes, approvals, or special instructions..."
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
      </div>
    </div>
  )
}
